import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Database } from '@/integrations/supabase/types';
import { pushClientError } from '@/utils/clientErrorBuffer';
// DTC-K: route security-critical support-session lifecycle through the
// canonical clock so the `ended_at` write, expiry-countdown, realtime
// revocation validity check, and active-session query bound are all
// deterministic under `clock.setFixed(...)`. Server-side RLS + DB
// expires_at remain authoritative for security; this is purely about
// test-freezability and audit-trail determinism.
import { clock } from '@/utils/clock';
import { toJson } from '@/utils/toJson';
// BJ-GAP-005: reads the server's aggregate support-access refusal. The bound
// is enforced in the database; this only decides what the operator is told.
import { describeSupportAccessLimit } from '@/utils/supportAccessLimit';
import { reportSupportAuditFailure, userModeDisplayExpiry } from '@/lib/support/supportAuditFailure';
// Core-100 R5 CORE-BJ / OD-49: the shop-authorised path (the database decides; this only calls it).
import { describeShopSupportAccessError, startGrantedSupportSessionRpc } from '@/lib/support/shopSupportAccess';

type Shop = Database['public']['Tables']['shops']['Row'];
type Membership = Database['public']['Tables']['memberships']['Row'];

interface SupportSession {
  sessionId: string;
  targetShopId: string;
  targetShop: Shop | null;
  targetMembership: Membership | null;
  mode: 'SHOP' | 'USER';
  triggeredFromBugId?: string;
  expiresAt: string;
  returnPath?: string; // Store where user was before entering support mode
  /** Core-100 R5 CORE-BJ / OD-49: set when the session was opened through a shop's approval (read-only view). */
  grantId?: string;
  grantScope?: string;
}

interface SupportModeContextType {
  isInSupportMode: boolean;
  supportSession: SupportSession | null;
  effectiveShop: Shop | null;
  effectiveMembership: Membership | null;
  canAccessSupportMode: boolean;
  timeRemaining: number | null; // seconds until expiry
  startSupportSession: (params: {
    targetShopId: string;
    /**
     * BJ-N5/BJ-N6: the disclosure-safe justification, REQUIRED before access
     * begins. Shown to the affected shop and person. Distinct from `note`,
     * which stays private — never pass private commentary here.
     */
    disclosureReason: string;
    mode?: 'SHOP' | 'USER';
    targetUserId?: string;
    triggeredFromBugId?: string;
    note?: string;
    returnPath?: string;
  }) => Promise<void>;
  /**
   * Core-100 R5 CORE-BJ / OD-49: open a shop through ITS approval. The server checks the platform switch, the
   * operator's designation and the shop's grant, opens exactly one session bounded by the grant and returns only the
   * shop's id and name. Resolves `{ ok: false, message }` with the server's refusal in words — never a silent no-op.
   */
  startGrantedSupportSession: (params: {
    grantId: string;
    disclosureReason: string;
    note?: string;
    returnPath?: string;
  }) => Promise<{ ok: boolean; message?: string }>;
  switchToMember: (userId: string) => Promise<void>;
  switchToShopView: () => Promise<void>;
  endSupportSession: () => Promise<void>;
  extendSession: () => Promise<void>;
}

const SupportModeContext = createContext<SupportModeContextType | undefined>(undefined);

export const SupportModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, activeShop, membership } = useAuth();
  const { toast } = useToast();
  const [supportSession, setSupportSession] = useState<SupportSession | null>(null);
  const [canAccessSupportMode, setCanAccessSupportMode] = useState(false);
  const [returnPath, setReturnPath] = useState<string>('/');
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  // Check if user can access support mode (internal shop + manager role).
  //
  // ARC-A3: deps tightened from `[user, activeShop, membership]` (full
  // objects) to `[user?.id, activeShop?.is_internal, membership?.role]`.
  // The access decision reads exactly two fields beyond nullness:
  // `activeShop.is_internal` and `membership.role`. Tightening to the
  // read fields means the effect re-fires only when the access decision
  // could actually change — not on every AuthProvider state ripple.
  // Behavior preserved: nullness transitions still flip the deps
  // (`undefined` ↔ `boolean` for is_internal; `undefined` ↔ role string
  // for role; `undefined` ↔ user.id string for user).
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) {
        setCanAccessSupportMode(false);
        return;
      }

      // CORE-BJ: support eligibility is the DEDICATED support-operator
      // designation, not a shop role. "MANAGER at an internal shop" is exactly
      // what the constitution's Inputs table excludes ("Not a role in the
      // shop's role model"), and it is why an internal manager could enter a
      // customer's shop while being unable to impersonate.
      //
      // UI only — the database is the boundary. RLS on support_sessions and the
      // impersonation RPC both ask is_support_operator independently.
      const { data: isOperator, error } = await (supabase as any).rpc('is_support_operator');
      if (error) {
        // Fail closed: an error must never read as eligible.
        console.warn('[SupportMode] support-operator check failed:', error);
        setCanAccessSupportMode(false);
        return;
      }
      setCanAccessSupportMode(isOperator === true);
    };

    checkAccess();
  }, [user?.id]);

  // FIX-R33-LOW-1: auto-end any active support session when the user signs
  // out. Without this, the local `supportSession` state persists through
  // the logout/login transition; the next user could momentarily render as
  // if the previous user's support session were active. Clear session
  // locally (and best-effort close the DB row) the instant `user` flips to
  // null.
  useEffect(() => {
    if (user) return;
    if (!supportSession) return;
    const sessionIdToClose = supportSession.sessionId;
    setSupportSession(null);
    // Best-effort DB close — anonymous caller, may fail under RLS; that's OK.
    supabase
      .from('support_sessions')
      .update({ active: false, ended_at: clock.now().toISOString() })
      .eq('id', sessionIdToClose)
      .then(({ error }) => {
        if (error) {
          console.warn('[SupportMode] post-logout session close failed (non-fatal):', error);
        }
      });
  }, [user, supportSession]);

  // Monitor session expiry
  useEffect(() => {
    if (!supportSession) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const expiresAt = new Date(supportSession.expiresAt);
      const now = clock.now();
      const secondsLeft = Math.floor((expiresAt.getTime() - now.getTime()) / 1000);

      setTimeRemaining(secondsLeft);

      if (secondsLeft <= 0) {
        endSupportSession();
        toast({
          title: 'Support Session Expired',
          description: 'Your support session has expired and has been ended.',
          variant: 'destructive',
        });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [supportSession]);

  // FIX-R34-HIGH-1: realtime subscription to support_sessions for the current
  // session id. If an admin revokes the session server-side (active=false) or
  // the DB expires it, we end the local context IMMEDIATELY instead of waiting
  // for the 1-second updateTimer tick to notice. Without this the support
  // operator could issue privileged reads for up to a second after revocation.
  useEffect(() => {
    if (!supportSession?.sessionId) return;
    const channel = supabase
      .channel(`support-session-${supportSession.sessionId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'support_sessions',
        filter: `id=eq.${supportSession.sessionId}`,
      }, (payload) => {
        const row: any = payload.new;
        if (!row) return;
        const isStillActive = row.active === true
          && (!row.expires_at || new Date(row.expires_at) > clock.now());
        if (!isStillActive) {
          console.log('[SupportMode] Session revoked or expired server-side, ending locally');
          setSupportSession(null);
          toast({
            title: 'Support Session Ended',
            description: 'Your support session was ended by the server.',
            variant: 'destructive',
          });
        }
      })
      .subscribe((status, err) => {
        // Chronomancer R-RT-6: security-critical stream. If this channel
        // drops silently, a revoke-at-server won't reach the client and
        // the support user retains elevated UI access until a reload.
        // Server-side RLS still enforces the revocation on any write,
        // but surfacing the drop lets oncall spot a stuck session.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[SupportMode] support-session realtime status:', status, err);
        }
      });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supportSession?.sessionId, toast]);

  // Check for active session on mount
  useEffect(() => {
    const checkActiveSession = async () => {
      if (!user || !canAccessSupportMode) return;

      // BJ-N3: recovery must be deterministic and honest about 0 / 1 / >1.
      //
      // This previously used `.maybeSingle()`, which ERRORS when more than one
      // row matches — so an operator holding two effective sessions silently
      // recovered NOTHING, with only a console message. The privileged
      // contexts kept running, unrepresented and, because the realtime
      // revocation channel watches a single session id, unrevocable in-band.
      //
      // Ordering is a TOTAL order (created_at, then id) so "the primary
      // session" is deterministic rather than whichever row the database
      // happened to return. `expires_at > now()` — not `active` alone — is what
      // makes a session effective; stale expired rows are not privileged
      // access and are excluded here exactly as before.
      const { data: rows, error } = await supabase
        .from('support_sessions')
        .select('*, shops(*)')
        .eq('started_by_user_id', user.id)
        .eq('active', true)
        .gt('expires_at', clock.now().toISOString())
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });

      if (error) {
        console.error('Error checking active session:', error);
        return;
      }

      const effective = rows ?? [];
      const data = effective[0] ?? null;

      // More than one effective session violates the BJ-N3 invariant. New ones
      // can no longer be created, so this can only be pre-existing data. Do not
      // hide it: recovering the newest keeps ONE context represented and
      // revocable, and the operator is told the rest exist rather than being
      // left to discover them. Nothing is ended, deleted or rewritten here —
      // the others remain valid evidence and expire under their own bound.
      if (effective.length > 1) {
        console.warn(
          '[SupportMode] multiple effective support sessions found; recovering the most recent',
          { count: effective.length, recovered: data?.id },
        );
        toast({
          title: 'Multiple support sessions found',
          description:
            `${effective.length} support sessions are open for your account. The most recent was restored; ` +
            'the others remain recorded and will end at their own expiry. End them from Support if that is not expected.',
          variant: 'destructive',
        });
      }

      if (data) {
        // Load membership for target shop or target user
        const targetUserId = data.target_user_id || user.id;
        const { data: targetMembership } = await supabase
          .from('memberships')
          .select('*')
          .eq('user_id', targetUserId)
          .eq('shop_id', data.target_shop_id)
          .eq('status', 'active')
          .maybeSingle();

        setSupportSession({
          sessionId: data.id,
          targetShopId: data.target_shop_id,
          targetShop: data.shops as Shop,
          targetMembership: targetMembership || null,
          mode: data.mode as 'SHOP' | 'USER',
          triggeredFromBugId: data.triggered_from_bug_id || undefined,
          expiresAt: data.expires_at,
        });
      }
    };

    checkActiveSession();
  }, [user, canAccessSupportMode]);

  const startSupportSession = async (params: {
    targetShopId: string;
    disclosureReason: string;
    mode?: 'SHOP' | 'USER';
    targetUserId?: string;
    triggeredFromBugId?: string;
    note?: string;
    returnPath?: string;
  }) => {
    if (!user || !canAccessSupportMode) {
      toast({
        title: 'Access Denied',
        description: 'You do not have permission to access support mode.',
        variant: 'destructive',
      });
      return;
    }

    // BJ-N5/BJ-N6: a reason must exist BEFORE privileged access begins. Refuse
    // locally rather than letting the database reject mid-flow, so no shop is
    // opened and no navigation occurs on a blank justification.
    const disclosureReason = (params.disclosureReason ?? '').trim();
    if (!disclosureReason) {
      toast({
        title: 'Reason required',
        description:
          'State why this access is needed. It is shown to the shop and to anyone whose account you enter.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Store current path if provided
      if (params.returnPath) {
        setReturnPath(params.returnPath);
      }

      // End any existing active sessions first
      await endSupportSession();

      // Fetch target shop details
      const { data: targetShop, error: shopError } = await supabase
        .from('shops')
        .select('*')
        .eq('id', params.targetShopId)
        .single();

      if (shopError || !targetShop) {
        throw new Error('Could not load target shop');
      }

      // Create new session
      const { data: session, error: sessionError } = await supabase
        .from('support_sessions')
        .insert({
          started_by_user_id: user.id,
          target_shop_id: params.targetShopId,
          target_user_id: params.targetUserId || null,
          mode: params.mode || 'SHOP',
          triggered_from_bug_id: params.triggeredFromBugId,
          note: params.note,
          // BJ-N5/BJ-N6: the disclosed justification. NEVER params.note — that
          // is private operator commentary. The database enforces non-empty on
          // insert, so this is defence in depth rather than the only check.
          disclosure_reason: disclosureReason,
        })
        .select()
        .single();

      if (sessionError || !session) {
        // BJ-N3: the database refuses a second EFFECTIVE session for this
        // operator (SQLSTATE 55006). `endSupportSession()` above only closes
        // the session THIS browser knows about, so the conflict is normally
        // another tab or device. Tell the operator that plainly instead of a
        // generic failure — and never report a start that did not happen.
        const conflictsWithExistingSession =
          (sessionError as { code?: string } | null)?.code === '55006' ||
          /already holds an effective support session/i.test(sessionError?.message ?? '');

        if (conflictsWithExistingSession) {
          toast({
            title: 'A support session is already open',
            description:
              'Your account already has an open support session, possibly in another browser or device. End it before starting a new one.',
            variant: 'destructive',
          });
          return;
        }

        // BJ-GAP-005: the database refuses a start that would exceed this
        // operator's rolling support-access budget (SQLSTATE 53400). The
        // server knows exactly which of the four bounds was hit and when the
        // operator becomes eligible again, so saying "Could not create support
        // session" here would be throwing away information the operator needs.
        // The DB is the enforcement boundary; this is only how it reads.
        const aggregateLimit = describeSupportAccessLimit(
          sessionError as { code?: string; details?: string } | null,
        );
        if (aggregateLimit) {
          toast({
            title: 'Support access limit reached',
            description: aggregateLimit,
            variant: 'destructive',
          });
          return;
        }

        throw new Error('Could not create support session');
      }

      // Load membership for target user or current user
      const targetUserId = params.targetUserId || user.id;
      const { data: targetMembership } = await supabase
        .from('memberships')
        .select('*')
        .eq('user_id', targetUserId)
        .eq('shop_id', params.targetShopId)
        .eq('status', 'active')
        .maybeSingle();

      setSupportSession({
        sessionId: session.id,
        targetShopId: params.targetShopId,
        targetShop: targetShop as Shop,
        targetMembership: targetMembership || null,
        mode: params.mode || 'SHOP',
        triggeredFromBugId: params.triggeredFromBugId,
        expiresAt: session.expires_at,
        returnPath: params.returnPath,
      });

      // P6-A6 — wire support-mode session START to canonical audit_log.
      //
      // Sister to P6-A2/A3/A4/A5. Action vocabulary: SUPPORT_MODE_STARTED
      // — fifth SCREAMING_SNAKE_CASE entry in the high-risk-admin-action
      // namespace P6-A2 established. This row brackets the in-session
      // audit trail already produced by useSupportAudit (which writes
      // per-action audit rows tagged with support_session_id during
      // the session). Together with the SUPPORT_MODE_ENDED row at end
      // time, compliance can pivot on actor_user_id + created_at +
      // support_session_id to reconstruct an entire support intervention.
      //
      // Real-actor doctrine (IV-D-23 R2 FIX-1): actor_user_id MUST be
      // the real internal admin (`user.id` from useAuth), NEVER the
      // impersonated target. The `started_by_user_id` column on
      // support_sessions already captures this correctly; we mirror
      // it here so platform-wide audit_log queries don't need a JOIN
      // through support_sessions to attribute the action.
      //
      // PII safety: `params.note` (operator commentary, potentially
      // sensitive context like "investigating customer complaint about
      // X") is INTENTIONALLY excluded from new_value. The note is
      // tracked in the client error buffer at L306-313 below for
      // post-incident reconstruction; the canonical audit_log row
      // captures only structural session-bracket facts (target shop,
      // mode, triggering bug if any). Same PII-safety design as
      // P6-A3/A4/A5.
      //
      // Non-blocking semantics: on audit insert failure, console.warn
      // and continue. The support session is already inserted in
      // support_sessions and the local state is set; the user-facing
      // toast still fires. Matches all 5 prior P6-A rounds.
      const auditNewValueStart: Record<string, unknown> = {
        support_session_id: session.id,
        target_shop_id: params.targetShopId,
        mode: params.mode || 'SHOP',
      };
      if (params.targetUserId) {
        auditNewValueStart.target_user_id = params.targetUserId;
      }
      if (params.triggeredFromBugId) {
        auditNewValueStart.triggered_from_bug_id = params.triggeredFromBugId;
      }
      // Core-100 R2 CORE-BJ: the bracket is written by the server (record_support_audit), which
      // takes the actor and shop from the session; a client insert claiming support context is
      // refused by the database.
      const { error: auditStartErr } = await supabase.rpc('record_support_audit' as never, {
        p_session_id: session.id,
        p_action: 'SUPPORT_MODE_STARTED',
        p_object_type: 'support_session',
        p_object_id: session.id,
        p_details: toJson(auditNewValueStart),
      } as never);
      if (auditStartErr) {
        // Core-100 R4 CORE-BJ (DoD 6): loud, through the same warn-once mechanism as every
        // support audit write — the session continues, but the operator is told it is unrecorded.
        reportSupportAuditFailure(session.id, 'the start of this support session', auditStartErr);
      }

      // FIX-R35-HIGH-8: audit trail — record the transition in the client
      // error buffer (label intentionally neutral) so any bug report filed
      // during this support session includes a trace of what context the
      // operator entered. support_sessions tracks the final state but not
      // the sequence of transitions.
      pushClientError({
        source: 'other',
        message: `[support] entered session ${session.id} targeting shop ${params.targetShopId} mode=${params.mode || 'SHOP'}`,
        shopId: params.targetShopId,
        supportSessionId: session.id,
        userId: user.id,
        route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });

      toast({
        title: 'Support Mode Activated',
        description: `Now viewing ${targetShop.name}`,
      });
    } catch (error) {
      console.error('Error starting support session:', error);
      toast({
        title: 'Error',
        description: 'Failed to start support mode session',
        variant: 'destructive',
      });
    }
  };

  // Core-100 R5 CORE-BJ / OD-49 — the shop-authorised path. The operator is NOT a member of the shop and no member
  // RLS policy is widened, so this session never becomes `isInSupportMode` (that needs a target membership); the
  // operator sees the shop only through the allowlisted, audited read RPC on the granted-shop page.
  const startGrantedSupportSession = async (params: {
    grantId: string;
    disclosureReason: string;
    note?: string;
    returnPath?: string;
  }): Promise<{ ok: boolean; message?: string }> => {
    if (!user || !canAccessSupportMode) {
      return { ok: false, message: 'Only a designated OptiBay support operator can open a shop.' };
    }
    const disclosureReason = (params.disclosureReason ?? '').trim();
    if (!disclosureReason) {
      return {
        ok: false,
        message: 'State why this access is needed. It is shown to the shop.',
      };
    }
    try {
      const started = await startGrantedSupportSessionRpc(params.grantId, disclosureReason, params.note);
      if (params.returnPath) setReturnPath(params.returnPath);
      setSupportSession({
        sessionId: started.session_id,
        targetShopId: started.shop_id,
        // Only what the server returned (id + name); never a full shops row the operator could not read.
        targetShop: { id: started.shop_id, name: started.shop_name ?? 'Shop' } as Shop,
        targetMembership: null,
        mode: 'SHOP',
        expiresAt: started.expires_at,
        returnPath: params.returnPath,
        grantId: started.grant_id,
        grantScope: started.scope,
      });
      pushClientError({
        source: 'other',
        message: `[support] opened granted session ${started.session_id} for shop ${started.shop_id} grant=${started.grant_id}`,
        shopId: started.shop_id,
        supportSessionId: started.session_id,
        userId: user.id,
        route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });
      return { ok: true };
    } catch (error) {
      const message = describeShopSupportAccessError(error as { message?: string })
        ?? describeSupportAccessLimit(error as { code?: string; details?: string } | null)
        ?? 'Support access could not be opened. Nothing was opened.';
      return { ok: false, message };
    }
  };

  const switchToMember = async (userId: string) => {
    if (!supportSession) return;

    try {
      // Load the target user's membership
      const { data: targetMembership } = await supabase
        .from('memberships')
        .select('*')
        .eq('user_id', userId)
        .eq('shop_id', supportSession.targetShopId)
        .eq('status', 'active')
        .maybeSingle();

      // SUP-9 (K84-B): SERVER-AUTHORITATIVE production transition. The edge
      // `support-impersonation-transition` delegates to a single transactional
      // RPC that writes the target-visible notice + the support_sessions USER
      // switch + the SUP-8 `SUPPORT_MODE_IMPERSONATED` audit row ATOMICALLY. In
      // production the client performs NONE of those writes — it only syncs
      // local UI state after the server confirms success. This removes the
      // K84-A interim risk where the notice could land without the matching
      // mode switch/audit (no transactional boundary).
      let serverTransitioned = false;
      try {
        const { data: txnRes, error: txnErr } = await supabase.functions.invoke(
          'support-impersonation-transition',
          {
            body: {
              supportSessionId: supportSession.sessionId,
              targetUserId: userId,
              shopId: supportSession.targetShopId,
            },
          },
        );
        serverTransitioned = !txnErr && !!(txnRes as { success?: boolean } | null)?.success;
        if (!serverTransitioned) {
          console.warn('[SupportMode.switchToMember] server transition not confirmed:', txnErr);
        }
      } catch (txnException) {
        console.warn('[SupportMode.switchToMember] server transition threw:', txnException);
      }

      if (serverTransitioned) {
        // Server owns notice + mode switch + audit (one transaction). The
        // client only reflects the new mode locally — it does NOT update
        // support_sessions or write audit_log again.
        //
        // Core-100 R4 CORE-BJ: person-scoped (USER) access has its own, tighter
        // bound, applied BY THE SERVER on entry (support_user_mode_max_lifetime,
        // migration 20270206020900). The client touches no support_sessions row
        // here (SUP-9); it only shows the bound the server applied, so the
        // countdown never promises more time than the server will honour.
        setSupportSession({
          ...supportSession,
          targetMembership: targetMembership || null,
          mode: 'USER',
          expiresAt: userModeDisplayExpiry(supportSession.expiresAt, clock.now()),
        });
        // FIX-R35-HIGH-8: local breadcrumb for within-session transitions.
        pushClientError({
          source: 'other',
          message: `[support] switchToMember session=${supportSession.sessionId} targetUser=${userId} role=${targetMembership?.role || 'unknown'}`,
          shopId: supportSession.targetShopId,
          supportSessionId: supportSession.sessionId,
          role: targetMembership?.role || null,
          route: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
        toast({
          title: 'Switched to Member View',
          description: `Now viewing as ${targetMembership?.role || 'user'}`,
        });
        return;
      }

      // Server transition did not complete — refused, in every environment.
      //
      // Production always refused it (K84-B): a client-orchestrated mode switch
      // is non-transactional, so it can leave the notice, the mode switch and
      // the audit row out of step with each other.
      //
      // BJ-GAP-005 removes the DEV/LOCAL fallback that used to run here. That
      // fallback wrote `mode='USER', target_user_id=…` straight onto the
      // session, and probing it against a real database showed what that
      // actually did: the person was impersonated with NO person-visible
      // notice, ZERO `SUPPORT_MODE_IMPERSONATED` audit rows, no membership
      // check, no BJ-N2 idempotency and no expiry check. The database now
      // refuses that write outright, so leaving the code here would only
      // produce a confusing failure. USER scope has exactly one entrance:
      // `support_impersonation_transition`.
      console.warn(
        '[SupportMode.switchToMember] server transition unavailable; USER scope refused (no client-orchestrated fallback exists)',
      );
      toast({
        title: 'Impersonation blocked',
        description:
          'The server could not record the member-visible impersonation transition, so "View as Member" was refused. Please retry; if it persists, contact platform support.',
        variant: 'destructive',
      });
      return;
    } catch (error) {
      console.error('Error switching to member:', error);
      toast({
        title: 'Error',
        description: 'Failed to switch to member view',
        variant: 'destructive',
      });
    }
  };

  const switchToShopView = async () => {
    if (!supportSession) return;

    try {
      const { error } = await supabase
        .from('support_sessions')
        .update({
          target_user_id: null,
          mode: 'SHOP',
        })
        .eq('id', supportSession.sessionId);

      if (error) throw error;

      setSupportSession({
        ...supportSession,
        targetMembership: null,
        mode: 'SHOP',
      });

      // FIX-R36-LOW-3: complete the support-transition audit trail.
      // Previously startSupportSession + switchToMember pushed audit
      // entries; switchToShopView did not, leaving a gap when an
      // operator dropped from USER mode back to SHOP mode.
      pushClientError({
        source: 'other',
        message: `[support] switchToShopView session=${supportSession.sessionId} (left USER mode)`,
        shopId: supportSession.targetShopId,
        supportSessionId: supportSession.sessionId,
        route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      });

      // SUP-8 (K37): durable audit_log row for the impersonation-exit
      // transition (was breadcrumb-only).
      // Core-100 R2 CORE-BJ: through the server door like every support audit row.
      const { error: auditExitErr } = await supabase.rpc('record_support_audit' as never, {
        p_session_id: supportSession.sessionId,
        p_action: 'SUPPORT_MODE_VIEW_SWITCHED',
        p_object_type: 'support_session',
        p_object_id: supportSession.sessionId,
        p_details: { mode: 'SHOP', left: 'USER' },
      } as never);
      if (auditExitErr) {
        reportSupportAuditFailure(supportSession.sessionId, 'leaving the member view', auditExitErr);
      }

      toast({
        title: 'Switched to Shop View',
        description: 'Now viewing at shop level',
      });
    } catch (error) {
      console.error('Error switching to shop view:', error);
      toast({
        title: 'Error',
        description: 'Failed to switch to shop view',
        variant: 'destructive',
      });
    }
  };

  const extendSession = async () => {
    if (!supportSession) return;
    // Core-100 R5 CORE-BJ / OD-49: a shop-authorised session ends with the shop's approval (the database refuses an
    // extension, SUPPORT_GRANT_BOUND). Say so instead of attempting it.
    if (supportSession.grantId) {
      toast({
        title: 'Cannot extend shop-authorised access',
        description: "This access ends when the shop's approval ends. Ask the shop for a new approval.",
        variant: 'destructive',
      });
      return;
    }

    try {
      const newExpiry = clock.now();
      newExpiry.setHours(newExpiry.getHours() + 4);

      const { error } = await supabase
        .from('support_sessions')
        .update({
          expires_at: newExpiry.toISOString(),
        })
        .eq('id', supportSession.sessionId);

      if (error) {
        // SUP-7 (K37): the support_sessions_max_lifetime CHECK constraint
        // (created_at + 24h) rejects an extension past the absolute cap.
        // Surface that distinctly so the operator starts a fresh session
        // rather than seeing a generic failure.
        const capHit =
          error.code === '23514' ||
          /support_sessions_max_lifetime|max_lifetime/i.test(error.message ?? '');
        // Core-100 R4 CORE-BJ: person-scoped (USER) access is bounded more tightly than shop access.
        const userModeCap = /SUPPORT_USER_MODE_MAX_LIFETIME/.test(error.message ?? '');
        // Core-100 R5 CORE-BJ / OD-49: a recovered shop-authorised session (after a reload) is bounded by the grant.
        const grantBound = /SUPPORT_GRANT_BOUND/.test(error.message ?? '');
        toast({
          title: capHit ? 'Session limit reached' : 'Error',
          description: grantBound
            ? "This access ends when the shop's approval ends. Ask the shop for a new approval."
            : userModeCap
            ? 'Viewing as a member is limited to a short, fixed time and cannot be extended. Switch back to the shop view, or end this session and start a new one.'
            : capHit
              ? 'This support session has reached its maximum 24-hour lifetime. Please start a new session.'
              : 'Failed to extend session',
          variant: 'destructive',
        });
        return;
      }

      setSupportSession({
        ...supportSession,
        expiresAt: newExpiry.toISOString(),
      });

      toast({
        title: 'Session Extended',
        description: 'Support session extended by 4 hours',
      });
    } catch (error) {
      console.error('Error extending session:', error);
      toast({
        title: 'Error',
        description: 'Failed to extend session',
        variant: 'destructive',
      });
    }
  };

  const endSupportSession = async () => {
    if (!supportSession) return;
    const sessionIdBeingClosed = supportSession.sessionId;

    try {
      // FIX-R35-MED-2: destructure and check the update error. Previously
      // this was fire-and-forget (no `error` destructured), so a silent
      // server-side failure left the session `active=true` on the server
      // while the client cleared local state — appearing as an active
      // session to anyone querying support_sessions.
      const { error: updateErr } = await supabase
        .from('support_sessions')
        .update({
          active: false,
          ended_at: clock.now().toISOString(),
        })
        .eq('id', sessionIdBeingClosed);

      if (updateErr) {
        console.error('[SupportMode] failed to mark support_sessions.active=false:', updateErr, {
          sessionId: sessionIdBeingClosed,
        });
        pushClientError({
          source: 'other',
          message: `[support] endSupportSession DB update failed: ${updateErr.message}`,
          supportSessionId: sessionIdBeingClosed,
        });
        toast({
          title: 'Support session ended locally',
          description: 'We cleared your local session but the server record could not be updated. An admin may need to close it.',
          variant: 'destructive',
        });
      } else {
        // P6-A6 — wire support-mode session END to canonical audit_log.
        //
        // Sister to SUPPORT_MODE_STARTED above (sixth SCREAMING_SNAKE_CASE
        // entry in the namespace). Inserted INSIDE the success branch
        // (updateErr was null) so audit only fires when support_sessions
        // was actually marked active=false on the server. The server-
        // side update-failed branch above (L482-495) already records
        // the failure via pushClientError + a destructive toast; we
        // do NOT mirror that into audit_log because the session was
        // not authoritatively closed.
        //
        // Real-actor doctrine: actor_user_id is the same `user.id` the
        // start row used. shop_id is `supportSession.targetShopId`
        // (closed-over from outer scope — supportSession is non-null
        // here because the early `if (!supportSession) return;` at
        // the top of endSupportSession guarded against the null case).
        //
        // changed_fields enumerates the support_sessions columns the
        // UPDATE actually touched (active, ended_at). Mirrors the
        // P6-A3/P6-A4/P6-A5 PII-safe field-name summary pattern.
        //
        // Non-blocking semantics: on audit insert failure, console.warn
        // and continue. The user-facing toast still fires; local state
        // is still cleared at L502 below.
        const { error: auditEndErr } = await supabase.rpc('record_support_audit' as never, {
          p_session_id: sessionIdBeingClosed,
          p_action: 'SUPPORT_MODE_ENDED',
          p_object_type: 'support_session',
          p_object_id: sessionIdBeingClosed,
          p_details: {
            ended_at: clock.now().toISOString(),
            changed_fields: ['active', 'ended_at'],
          },
        } as never);
        if (auditEndErr) {
          reportSupportAuditFailure(sessionIdBeingClosed, 'the end of this support session', auditEndErr);
        }

        toast({
          title: 'Support Mode Ended',
          description: 'Returned to normal view',
        });
      }
      setSupportSession(null);
    } catch (error) {
      console.error('Error ending support session:', error);
      // Still clear local state even if update fails
      setSupportSession(null);
    }
  };

  // FIX-R34-HIGH-2: only expose effective context when BOTH targetShop AND
  // targetMembership resolved. If the target user's membership has been
  // revoked since the session was minted, the membership load returns null
  // and callers must fall back to the support operator's real activeShop
  // instead of operating on the target shop with no role metadata.
  const hasFullyResolvedTarget = !!(supportSession?.targetShop && supportSession?.targetMembership);
  const value: SupportModeContextType = {
    isInSupportMode: !!supportSession && hasFullyResolvedTarget,
    supportSession,
    effectiveShop: hasFullyResolvedTarget ? (supportSession?.targetShop ?? null) : null,
    effectiveMembership: hasFullyResolvedTarget ? (supportSession?.targetMembership ?? null) : null,
    canAccessSupportMode,
    timeRemaining,
    startSupportSession,
    startGrantedSupportSession,
    switchToMember,
    switchToShopView,
    endSupportSession,
    extendSession,
  };

  return (
    <SupportModeContext.Provider value={value}>
      {children}
    </SupportModeContext.Provider>
  );
};

export const useSupportMode = () => {
  const context = useContext(SupportModeContext);
  if (context === undefined) {
    throw new Error('useSupportMode must be used within a SupportModeProvider');
  }
  return context;
};
